// Utils
import { getNotificationContent } from '../common/helpers/notificationContent.helper'
import {
  formatRelativeTime,
  getTimeGroup,
  TimeGroup,
  toDate,
} from './dateHelper'

// Types
import { IconType } from '../components/icon/IconType'
import { ICompanyMemberPure } from '../common/models/Company'
import {
  INotificationPure,
  NotificationType,
} from '../common/models/Notification'
import {
  IRequest,
  RequestType,
  requestIsFillSurvey,
  requestOpensModal,
} from '../common/models/Request'

type TranslateFn = (key: string, options?: Record<string, any>) => string

/** Minimal shape needed to name a term, from company.termsInformation.terms. */
export interface ITermName {
  termNumber: number
  name?: string
}

export type ActivityKind = 'request' | 'notification'

/**
 * One row in the activity panel. Both notifications and requests map onto this
 * shape so a single card renders either; neither underlying model changes.
 */
export interface IActivityItem {
  id: string
  kind: ActivityKind
  icon: IconType
  title: string
  /** Extra subtitle fragment between the person and the timestamp. */
  detail?: string
  personName?: string
  /** Whether the person did this ("by Anna") or it is about them ("from Anna"). */
  personIsActor?: boolean
  person?: ICompanyMemberPure
  otherPeople: ICompanyMemberPure[]
  url?: string
  /** Requests only: router state handed to the page the row opens. */
  linkState?: IRequestLinkState
  /** Requests only: the row opens this request's modal in place (RequestModals). */
  modalRequest?: IRequest
  date: Date
  isUnread: boolean
  /** Requests only: the row is a finished to-do, shown behind the panel's toggle. */
  isCompleted?: boolean
  timeGroup: TimeGroup
  /** Same key = same bundle within a time group. Undefined never bundles. */
  bundleKey?: string
  /** Notification ids this row covers, for marking read. Empty for requests. */
  notificationIds: string[]
}

/**
 * Type icon per notification. A full Record on purpose: adding a NotificationType
 * without an icon is then a compile error rather than a row that ships blank.
 */
export const NOTIFICATION_ICON: Record<NotificationType, IconType> = {
  [NotificationType.FEEDBACK_RECEIVED]: 'Feedback',
  [NotificationType.FEEDBACK_RECEIVED_EXTERNAL]: 'ExternPeople',
  [NotificationType.FEEDBACK_REVOKED]: 'Feedback',
  [NotificationType.GOAL_COMMENT]: 'Goals',
  [NotificationType.GOALS_REMINDER]: 'Goals',
  [NotificationType.GOALS_UNPUBLISHED_TEAM_LEAD]: 'Goals',
  [NotificationType.GOALS_UNPUBLISHED_CUSTOM_LEAD]: 'Goals',
  [NotificationType.JOURNAL_INACTIVITY]: 'Journal',
  [NotificationType.JOURNAL_ACCESS_LOST]: 'Lock',
  [NotificationType.JOURNAL_NEW_ENTRY]: 'Journal',
  [NotificationType.COLLECTION_NEW]: 'Card',
  [NotificationType.COLLECTION_NOTE]: 'Card',
  [NotificationType.EVALUATION_REVOKED]: 'Evaluation',
  [NotificationType.EVALUATION_RECEIVED]: 'Evaluation',
  [NotificationType.BECAME_CUSTOM_LEAD]: 'Lead',
  [NotificationType.STOPPED_BEING_CUSTOM_LEAD]: 'Lead',
  [NotificationType.BECAME_LEAD]: 'Lead',
  [NotificationType.BECAME_BUDDY]: 'Buddy',
  [NotificationType.NEW_MEMBER_IN_TEAM]: 'People',
  [NotificationType.ONBOARDING_RUNNING_LATE]: 'Overdue',
  [NotificationType.ONBOARDING_COMPLETE]: 'Onboarding',
  [NotificationType.ONBOARDING_PROGRESS]: 'Onboarding',
  [NotificationType.ONBOARDING_STARTED]: 'Onboarding',
  [NotificationType.EVALUATION_RESULTS_AVAILABLE]: 'Evaluation',
  [NotificationType.GOALS_REVIEWED]: 'Goals',
  [NotificationType.GOALS_CARRIED_OVER]: 'Goals',
  [NotificationType.JOURNAL_ACCESS_GRANTED]: 'Journal',
  [NotificationType.BECAME_ADMIN]: 'Admin',
  [NotificationType.STOPPED_BEING_ADMIN]: 'Admin',
  [NotificationType.GAINED_VIEW_ALL]: 'Show',
  [NotificationType.LOST_VIEW_ALL]: 'Show',
  [NotificationType.TEAM_GOALS_PUBLISHED]: 'Goals',
  [NotificationType.TEAM_SELF_EVALUATION_SUBMITTED]: 'Evaluation',
  [NotificationType.TEAM_FEEDBACK_EXCHANGED]: 'Feedback',
  [NotificationType.COMPANY_MEMBER_JOINED]: 'Company',
  [NotificationType.COMPANY_MEMBER_ARCHIVED]: 'Archive',
  [NotificationType.COMPANY_TERM_STARTED]: 'Calendar',
  [NotificationType.COMPANY_EVALUATIONS_STARTED]: 'Evaluation',
  [NotificationType.COMPANY_EVALUATIONS_CLOSED]: 'Evaluation',
  [NotificationType.COMPANY_SURVEY_COMPLETED]: 'Statistics',
  [NotificationType.TEAMMATE_JOINED]: 'People',
  [NotificationType.TEAMMATE_LEFT]: 'People',
  [NotificationType.JOB_UPDATED]: 'Job',
}

/**
 * In-app title per notification, deliberately name-free: the person moves to the
 * subtitle. The weekly summary email keeps its own name-inline copy under
 * `emails.summary.*` — do not repoint the email at these keys.
 */
export const NOTIFICATION_TITLE: Record<NotificationType, string> = {
  [NotificationType.FEEDBACK_RECEIVED]: 'notifications.titles.feedbackReceived',
  [NotificationType.FEEDBACK_RECEIVED_EXTERNAL]:
    'notifications.titles.feedbackReceivedExternal',
  [NotificationType.FEEDBACK_REVOKED]: 'notifications.titles.feedbackRevoked',
  [NotificationType.GOAL_COMMENT]: 'notifications.titles.goalComment',
  [NotificationType.GOALS_REMINDER]: 'notifications.titles.goalsReminder',
  [NotificationType.GOALS_UNPUBLISHED_TEAM_LEAD]:
    'notifications.titles.goalsUnpublishedTeamLead',
  [NotificationType.GOALS_UNPUBLISHED_CUSTOM_LEAD]:
    'notifications.titles.goalsUnpublishedCustomLead',
  [NotificationType.JOURNAL_INACTIVITY]:
    'notifications.titles.journalInactivity',
  [NotificationType.JOURNAL_ACCESS_LOST]:
    'notifications.titles.journalAccessLost',
  [NotificationType.JOURNAL_NEW_ENTRY]: 'notifications.titles.journalNewEntry',
  [NotificationType.COLLECTION_NEW]: 'notifications.titles.collectionNew',
  [NotificationType.COLLECTION_NOTE]: 'notifications.titles.collectionNote',
  [NotificationType.EVALUATION_REVOKED]:
    'notifications.titles.evaluationRevoked',
  [NotificationType.EVALUATION_RECEIVED]:
    'notifications.titles.evaluationReceived',
  [NotificationType.BECAME_CUSTOM_LEAD]:
    'notifications.titles.becameCustomLead',
  [NotificationType.STOPPED_BEING_CUSTOM_LEAD]:
    'notifications.titles.stoppedBeingCustomLead',
  [NotificationType.BECAME_LEAD]: 'notifications.titles.becameLead',
  [NotificationType.BECAME_BUDDY]: 'notifications.titles.becameBuddy',
  [NotificationType.NEW_MEMBER_IN_TEAM]: 'notifications.titles.newMemberInTeam',
  [NotificationType.ONBOARDING_RUNNING_LATE]:
    'notifications.titles.onboardingRunningLate',
  [NotificationType.ONBOARDING_COMPLETE]:
    'notifications.titles.onboardingComplete',
  [NotificationType.ONBOARDING_PROGRESS]:
    'notifications.titles.onboardingProgress',
  [NotificationType.ONBOARDING_STARTED]:
    'notifications.titles.onboardingStarted',
  [NotificationType.EVALUATION_RESULTS_AVAILABLE]:
    'notifications.titles.evaluationResultsAvailable',
  [NotificationType.GOALS_REVIEWED]: 'notifications.titles.goalsReviewed',
  [NotificationType.GOALS_CARRIED_OVER]:
    'notifications.titles.goalsCarriedOver',
  [NotificationType.JOURNAL_ACCESS_GRANTED]:
    'notifications.titles.journalAccessGranted',
  [NotificationType.BECAME_ADMIN]: 'notifications.titles.becameAdmin',
  [NotificationType.STOPPED_BEING_ADMIN]:
    'notifications.titles.stoppedBeingAdmin',
  [NotificationType.GAINED_VIEW_ALL]: 'notifications.titles.gainedViewAll',
  [NotificationType.LOST_VIEW_ALL]: 'notifications.titles.lostViewAll',
  [NotificationType.TEAM_GOALS_PUBLISHED]:
    'notifications.titles.teamGoalsPublished',
  [NotificationType.TEAM_SELF_EVALUATION_SUBMITTED]:
    'notifications.titles.teamSelfEvaluationSubmitted',
  [NotificationType.TEAM_FEEDBACK_EXCHANGED]:
    'notifications.titles.teamFeedbackExchanged',
  [NotificationType.COMPANY_MEMBER_JOINED]:
    'notifications.titles.companyMemberJoined',
  [NotificationType.COMPANY_MEMBER_ARCHIVED]:
    'notifications.titles.companyMemberArchived',
  [NotificationType.COMPANY_TERM_STARTED]:
    'notifications.titles.companyTermStarted',
  [NotificationType.COMPANY_EVALUATIONS_STARTED]:
    'notifications.titles.companyEvaluationsStarted',
  [NotificationType.COMPANY_EVALUATIONS_CLOSED]:
    'notifications.titles.companyEvaluationsClosed',
  [NotificationType.COMPANY_SURVEY_COMPLETED]:
    'notifications.titles.companySurveyCompleted',
  [NotificationType.TEAMMATE_JOINED]: 'notifications.titles.teammateJoined',
  [NotificationType.TEAMMATE_LEFT]: 'notifications.titles.teammateLeft',
  [NotificationType.JOB_UPDATED]: 'notifications.titles.jobUpdated',
}

/**
 * Types that are no longer produced but still exist as historical rows. The enum
 * value must stay so old documents keep classifying; they are simply not shown.
 */
export const HIDDEN_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.GOALS_REMINDER,
]

/**
 * Types where the person performed the action rather than being its subject.
 * "Evaluation completed · from Anna" reads as Anna's evaluation; she actually
 * completed yours, so these get "by Anna" instead.
 */
const ACTOR_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.GOAL_COMMENT,
  NotificationType.JOURNAL_NEW_ENTRY,
  NotificationType.COLLECTION_NOTE,
  NotificationType.EVALUATION_RECEIVED,
  NotificationType.GOALS_REVIEWED,
  NotificationType.TEAM_FEEDBACK_EXCHANGED,
  NotificationType.JOB_UPDATED,
]

/** Types where several rows in one time group say the same thing about different people. */
const BUNDLABLE_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.FEEDBACK_RECEIVED,
  NotificationType.FEEDBACK_RECEIVED_EXTERNAL,
  NotificationType.FEEDBACK_REVOKED,
  NotificationType.GOAL_COMMENT,
  NotificationType.JOURNAL_NEW_ENTRY,
  NotificationType.COLLECTION_NOTE,
  NotificationType.EVALUATION_RECEIVED,
  NotificationType.EVALUATION_REVOKED,
  NotificationType.BECAME_CUSTOM_LEAD,
  NotificationType.STOPPED_BEING_CUSTOM_LEAD,
  NotificationType.NEW_MEMBER_IN_TEAM,
  NotificationType.ONBOARDING_STARTED,
  NotificationType.ONBOARDING_COMPLETE,
  NotificationType.ONBOARDING_RUNNING_LATE,
  NotificationType.TEAM_GOALS_PUBLISHED,
  NotificationType.TEAM_SELF_EVALUATION_SUBMITTED,
  NotificationType.TEAM_FEEDBACK_EXCHANGED,
  NotificationType.COMPANY_MEMBER_JOINED,
  NotificationType.COMPANY_MEMBER_ARCHIVED,
  NotificationType.TEAMMATE_JOINED,
  NotificationType.TEAMMATE_LEFT,
]

export const REQUEST_ICON: Record<RequestType, IconType> = {
  [RequestType.SET_GOALS]: 'Goals',
  [RequestType.SET_GOALS_SKIPPED_EVALUATION]: 'Goals',
  [RequestType.SET_GOALS_EARLY_EVALUATION]: 'Goals',
  [RequestType.REVIEW_GOALS]: 'Goals',
  [RequestType.LEAVE_FEEDBACK]: 'GiveFeedback',
  [RequestType.SPECIFY_CLOSE_MEMBERS]: 'People',
  [RequestType.EVALUATE]: 'Evaluation',
  [RequestType.CLOSE_EVALUATION]: 'Evaluation',
  [RequestType.START_ONBOARDING]: 'Onboarding',
  [RequestType.PLAN_EVALUATIONS]: 'Calendar',
  [RequestType.COMPLETE_PROFILE]: 'User',
  [RequestType.DELETE_ARCHIVED_MEMBERS]: 'TrashCan',
  [RequestType.FILL_SURVEY]: 'Statistics',
  [RequestType.COMPLETE_COMPANY_PROFILE]: 'Company',
}

/**
 * Existing `requests.*` copy is reused verbatim. For a to-do the target person is
 * part of the action ("leave feedback for Anna"), so unlike notifications the name
 * stays in the title rather than moving to the subtitle.
 */
export const REQUEST_TITLE: Record<RequestType, string> = {
  [RequestType.SET_GOALS]: 'requests.requestPublishGoals',
  [RequestType.SET_GOALS_SKIPPED_EVALUATION]: 'requests.requestPublishGoals',
  [RequestType.SET_GOALS_EARLY_EVALUATION]: 'requests.requestPublishGoals',
  [RequestType.REVIEW_GOALS]: 'requests.requestCheckGoals',
  [RequestType.LEAVE_FEEDBACK]: 'requests.requestLeaveFeedback',
  [RequestType.SPECIFY_CLOSE_MEMBERS]: 'requests.requestSuggestions',
  [RequestType.EVALUATE]: 'requests.requestCompleteAssessments',
  [RequestType.CLOSE_EVALUATION]: 'requests.requestCloseEvaluations',
  [RequestType.START_ONBOARDING]: 'requests.requestOnboarding',
  [RequestType.PLAN_EVALUATIONS]: 'requests.requestPlanEvaluations',
  [RequestType.COMPLETE_PROFILE]: 'requests.requestCompleteProfile',
  [RequestType.DELETE_ARCHIVED_MEMBERS]: 'requests.deleteArchived',
  [RequestType.FILL_SURVEY]: 'requests.requestFillSurvey',
  [RequestType.COMPLETE_COMPANY_PROFILE]: 'requests.completeCompany',
}

/** Router state a request row hands the page it opens. */
export interface IRequestLinkState {
  /** Term to preselect, a string to match IHistoryState in navHelper. */
  termNumber?: string
}

export interface IRequestTarget {
  pathname: string
  state?: IRequestLinkState
}

/**
 * Where a request row navigates, mirroring the handlers in Requests.tsx. Modal
 * requests have no page: they open in place through RequestModals, so they return
 * undefined here (see requestOpensModal).
 */
export const getRequestTarget = (
  request: IRequest
): IRequestTarget | undefined => {
  switch (request.actionType) {
    case RequestType.SET_GOALS:
    case RequestType.SET_GOALS_SKIPPED_EVALUATION:
    case RequestType.SET_GOALS_EARLY_EVALUATION:
      return { pathname: '/profile/goals' }
    case RequestType.REVIEW_GOALS:
      return {
        pathname: request.targetMemberId
          ? `/company/people/profile/${request.targetMemberId}/goals`
          : '/team',
      }
    case RequestType.LEAVE_FEEDBACK:
      return {
        pathname: request.feedbackId
          ? `/feedback/form/${request.feedbackId}`
          : '/',
      }
    case RequestType.CLOSE_EVALUATION:
    case RequestType.PLAN_EVALUATIONS:
      // The term travels along, as it does in Requests.tsx: without it the page
      // opens on its default term rather than the one the request is about.
      return {
        pathname: '/company/rituals/evaluations',
        state: { termNumber: '' + request.termNumber },
      }
    case RequestType.START_ONBOARDING:
      return { pathname: '/profile/onboarding' }
    case RequestType.COMPLETE_PROFILE:
      return {
        pathname: request.targetMemberId
          ? `/company/people/update/${request.targetMemberId}`
          : '/company/people',
      }
    case RequestType.FILL_SURVEY:
      return {
        pathname: request.distributionId
          ? `/survey/${request.distributionId}`
          : '/',
      }
    case RequestType.EVALUATE:
    case RequestType.SPECIFY_CLOSE_MEMBERS:
    case RequestType.DELETE_ARCHIVED_MEMBERS:
    case RequestType.COMPLETE_COMPANY_PROFILE:
      return undefined
  }
}

/**
 * The `{{name}}` in the reused `requests.*` copy is not always a person: the
 * evaluation requests interpolate the term name, and leave-feedback uses the full
 * name where the others use a first name. Getting this wrong renders "Sluit Anna".
 */
const getRequestTitleParams = (
  request: IRequest,
  target: ICompanyMemberPure | undefined,
  terms: ITermName[]
): Record<string, any> => {
  switch (request.actionType) {
    case RequestType.EVALUATE:
    case RequestType.CLOSE_EVALUATION:
    case RequestType.PLAN_EVALUATIONS:
      return {
        name: terms.find((term) => term.termNumber === request.termNumber)
          ?.name,
      }
    case RequestType.LEAVE_FEEDBACK:
      return { name: target?.name }
    case RequestType.REVIEW_GOALS:
    case RequestType.COMPLETE_PROFILE:
      return { name: target?.firstName }
    case RequestType.FILL_SURVEY:
      return {
        name: requestIsFillSurvey(request) ? request.surveyName : undefined,
      }
    default:
      return {}
  }
}

const findMember = (
  members: ICompanyMemberPure[],
  memberId?: string
): ICompanyMemberPure | undefined =>
  memberId ? members.find((m) => '' + m._id === '' + memberId) : undefined

/**
 * Title overrides for the three types whose copy differs per instance: an
 * evaluation revoke with or without a name, and the two onboarding types that read
 * differently when they are about the viewer themselves.
 */
const getTitleKey = (
  notification: INotificationPure,
  isSelf: boolean
): string => {
  switch (notification.type) {
    case NotificationType.EVALUATION_REVOKED:
      return notification.initiator?.name
        ? 'notifications.titles.evaluationRevoked'
        : 'notifications.titles.evaluationsClosed'
    case NotificationType.ONBOARDING_PROGRESS:
      return isSelf
        ? 'notifications.titles.onboardingProgressSelf'
        : 'notifications.titles.onboardingProgress'
    case NotificationType.ONBOARDING_RUNNING_LATE:
      return isSelf
        ? 'notifications.titles.onboardingRunningLateSelf'
        : 'notifications.titles.onboardingRunningLate'
    default:
      return NOTIFICATION_TITLE[notification.type]
  }
}

export const notificationToActivityItem = (
  notification: INotificationPure,
  members: ICompanyMemberPure[],
  t: TranslateFn
): IActivityItem | null => {
  // Unknown types reach production whenever a client runs behind the backend, and
  // hidden types still exist as historical rows. Neither may render.
  if (
    !NOTIFICATION_TITLE[notification.type] ||
    HIDDEN_NOTIFICATION_TYPES.includes(notification.type)
  ) {
    return null
  }

  const { params, url } = getNotificationContent(notification)
  const isSelf =
    '' + notification.initiator?.memberId === '' + notification.memberId
  const person = findMember(members, notification.initiator?.memberId)
  const date = toDate(notification.dateCreated)
  const timeGroup = getTimeGroup(date)

  // A second clause belongs in the subtitle, not stapled onto the title with a comma.
  const DETAIL_KEY: Partial<Record<NotificationType, string>> = {
    [NotificationType.ONBOARDING_PROGRESS]:
      'notifications.general.onboardingProgress',
    [NotificationType.COLLECTION_NEW]: 'notifications.general.notCompleteYet',
    [NotificationType.GOALS_CARRIED_OVER]:
      'notifications.general.canAddNewGoals',
    // Old job_updated rows carry no jobTitle, and "now " alone says nothing.
    ...(params.jobTitle && {
      [NotificationType.JOB_UPDATED]: 'notifications.general.newJob',
    }),
  }
  const detailKey = DETAIL_KEY[notification.type]
  const detail = detailKey ? t(detailKey, params) : undefined

  return {
    id: '' + notification._id,
    kind: 'notification',
    icon: NOTIFICATION_ICON[notification.type],
    title: t(getTitleKey(notification, isSelf), params),
    detail,
    // Self-referential onboarding rows are about the viewer, so no "from X".
    personName: isSelf ? undefined : notification.initiator?.name,
    personIsActor: ACTOR_NOTIFICATION_TYPES.includes(notification.type),
    person: isSelf ? undefined : person,
    otherPeople: [],
    url: url || undefined,
    date,
    isUnread: !notification.dateOpened,
    timeGroup,
    // Self-ness is part of the key: ONBOARDING_RUNNING_LATE fires both about the
    // viewer and about the people they lead, and those rows carry a different
    // title. Bundling them together showed the self title above a list of other
    // people, with the name placeholder left unresolved.
    bundleKey: BUNDLABLE_NOTIFICATION_TYPES.includes(notification.type)
      ? `${notification.type}|${isSelf}|${timeGroup}`
      : undefined,
    notificationIds: ['' + notification._id],
  }
}

export const requestToActivityItem = (
  request: IRequest,
  members: ICompanyMemberPure[],
  terms: ITermName[],
  t: TranslateFn
): IActivityItem => {
  const target = findMember(members, request.targetMemberId)
  // A completed row is history, not a to-do: its destination (a submitted feedback
  // form, a closed evaluation) is no longer somewhere to send anyone.
  const destination = request.completed ? undefined : getRequestTarget(request)
  const modalRequest =
    !request.completed && requestOpensModal(request) ? request : undefined
  // A finished to-do is interesting for when it was done, not when it was asked.
  const date = toDate(
    request.completed && request.dateCompleted
      ? request.dateCompleted
      : request.dateCreated
  )

  return {
    id: '' + request._id,
    kind: 'request',
    icon: REQUEST_ICON[request.actionType],
    title: t(
      REQUEST_TITLE[request.actionType],
      getRequestTitleParams(request, target, terms)
    ),
    // Deliberately no deadline in this slot. Requests.tsx derives it from term steps
    // and per-company `requestDeadlines`; recomputing that would give a second, subtly
    // different answer, and a wrong deadline is worse than none. The dashboard this row
    // links to shows the real one. Finished rows use the slot to say so instead.
    detail: request.completed
      ? t('general.states.completed').toLowerCase()
      : undefined,
    person: target,
    otherPeople: [],
    url: destination?.pathname,
    linkState: destination?.state,
    modalRequest,
    date,
    // A request is open or completed; it has no read state and never bundles.
    isUnread: false,
    isCompleted: !!request.completed,
    timeGroup: getTimeGroup(date),
    notificationIds: [],
  }
}

/**
 * Collapse rows that say the same thing about different people within one time
 * group into a single row ("Feedback received — from Anna and 2 others"). The
 * newest row wins the timestamp; every covered id is kept so marking read still
 * covers the whole bundle.
 */
export const bundleActivityItems = (
  items: IActivityItem[]
): IActivityItem[] => {
  const bundles = new Map<string, IActivityItem>()
  const result: IActivityItem[] = []

  items.forEach((item) => {
    if (!item.bundleKey) {
      result.push(item)
      return
    }

    const existing = bundles.get(item.bundleKey)
    if (!existing) {
      bundles.set(item.bundleKey, item)
      result.push(item)
      return
    }

    if (item.person) {
      existing.otherPeople.push(item.person)
    }
    existing.notificationIds.push(...item.notificationIds)
    existing.isUnread = existing.isUnread || item.isUnread
  })

  return result
}

/** Subtitle line: who it is about, any extra detail, and how long ago. */
export const getActivitySubtitle = (
  item: IActivityItem,
  t: TranslateFn
): string => {
  const prefix = item.personIsActor ? 'by' : 'from'
  const person = item.otherPeople.length
    ? t(`notifications.general.${prefix}People`, {
        name: item.personName,
        count: item.otherPeople.length,
      })
    : item.personName
    ? t(`notifications.general.${prefix}Person`, { name: item.personName })
    : undefined

  return [person, item.detail, formatRelativeTime(item.date, t)]
    .filter(Boolean)
    .join(' · ')
}
